import type {
  ContractObligation,
  ContractObligationList,
  ContractObligationStatus,
  ContractResponsibilities,
} from "@closer/shared";
import { CONTRACT_OBLIGATION_STATUS_NAMES } from "@closer/shared";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormField,
  Input,
  Select,
  Skeleton,
  useToast,
} from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { StateView } from "../../components/StateView";
import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { formatDate } from "../../lib/format";
import { usePreferences } from "../../lib/preferences";
import { sozlesmeAdaylariSorgusu } from "../../lib/sharedQueries";
import { tenantKey } from "../../lib/tenantQuery";
import { resolveErrorState } from "../../lib/uiState";

/**
 * SÖZLEŞME YÜKÜMLÜLÜKLERİ (CLOS-231 `[196]`, alt görev CLOS-1032).
 *
 * ### ⚠ Sorumlu atanması ERİŞİM VERMİYOR
 *
 * Kart: *"yükümlülük sorumluluğu sözleşme kayıt erişimini otomatik vermez."* Sorumlu
 * seçimi yalnız aktif üyeler arasından yapılıyor (`ContractResponsibleDialog` ile aynı desen)
 * ve bu bölüm hiçbir yerde "erişim verildi" demiyor.
 *
 * ### ⚠ AI önerisi burada YOK
 *
 * Bugün hiçbir AI üretici çalışmıyor (CLOS-205–208 build-time önkoşulu değil); bu
 * ekran yalnız MANUEL oluşturma ve durum geçişlerini sunuyor. Öneri kabul ucu
 * (`POST .../accept`) CLOS-1117 · yetim uç I ile KALDIRILDI; AI kartı geldiğinde kabul
 * akışı kendi kartıyla gelir (ADR-0014).
 */

const REPEAT_LABELS: Record<string, string> = {
  none: "Tekrarsız",
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
  yearly: "Yıllık",
};

const STATUS_TONE: Record<ContractObligationStatus, "success" | "warning" | "neutral"> = {
  open: "warning",
  completed: "success",
  cancelled: "neutral",
};

const TRANSITIONS: Record<
  ContractObligationStatus,
  { to: ContractObligationStatus; label: string }[]
> = {
  open: [
    { to: "completed", label: "Tamamlandı olarak işaretle" },
    { to: "cancelled", label: "İptal et" },
  ],
  completed: [{ to: "open", label: "Yeniden aç" }],
  cancelled: [{ to: "open", label: "Yeniden aç" }],
};

export function ContractObligationsSection({
  contractId,
  kayitYazma = true,
}: {
  contractId: string;
  kayitYazma?: boolean;
}) {
  const { activeTenantId, can } = useAuth();
  const tenantId = activeTenantId!;
  const { preferences } = usePreferences();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [formAcik, setFormAcik] = useState(false);

  /**

   * DÜZENLENEN yükümlülüğün kaydı; `null` ise form YENİ kayıt açıyor (CLOS-1109).

   *

   * ⚠ Aynı form iki iş yapıyor ve bu bilinçli: alanlar birebir aynı (`update` şeması

   * `create` şemasının kendisi + `revision`). İkinci bir form yazmak, iki yerde

   * güncellenmesi gereken tek bir kural üretirdi (kriter 6).

   */

  const [duzenlenen, setDuzenlenen] = useState<ContractObligation | null>(null);
  const [baslik, setBaslik] = useState("");
  const [sonTarih, setSonTarih] = useState("");
  const [tekrar, setTekrar] = useState<"none" | "daily" | "weekly" | "monthly" | "yearly">("none");
  const [tekrarAraligi, setTekrarAraligi] = useState("1");
  const [sorumlu, setSorumlu] = useState("");

  /** ⚠ Varsayılan `true`: alan yoksa yazabilir sayılıyor (`ContractAttachments` ile aynı gerekçe). */
  const yazabilir = can("contract.edit") && kayitYazma;
  /**
   * FİNANS SORUMLUSU ÖNERİSİ (CLOS-1126 · Halit K4): yeni yükümlülük açılırken sorumlu alanı
   * boşsa Finans Sorumlusu'nun üyeliği önceden seçilir. Yalnız prefill — kullanıcı
   * değiştirebilir, yetki ya da kayıt erişimi üretmez; mevcut yükümlülüğün AÇIK sorumlusu
   * düzenlemede daima üstün (oraya dokunulmaz). Sayfadaki sorumluluk listesiyle aynı anahtar.
   */
  const sorumlular = useQuery({
    enabled: yazabilir,
    queryKey: tenantKey(tenantId, "contract", contractId, "sorumlular"),
    queryFn: async () =>
      (
        await api<ContractResponsibilities>(`/api/contracts/${contractId}/responsibilities`, {
          tenantId,
        })
      ).data,
    retry: false,
  });
  const finansOnerisi =
    sorumlular.data?.contributors.find(
      (k) => k.area === "finance" && k.membershipStatus === "active",
    )?.membershipId ?? "";
  /**
   * Öneri form AÇIKKEN ve YENİ kayıtta bir kez uygulanır: sorumluluk sorgusu tıklamadan sonra
   * gelse de boş alan dolar; kullanıcı "Seçilmedi"ye çevirirse yeniden dayatılmaz (ref).
   * Düzenlemede (`duzenlenen` dolu) HİÇ çalışmaz — yükümlülüğün açık sorumlusu üstündür.
   */
  const oneriUygulandi = useRef(false);
  useEffect(() => {
    if (!formAcik || duzenlenen !== null || oneriUygulandi.current || finansOnerisi === "") return;
    oneriUygulandi.current = true;
    setSorumlu((onceki) => (onceki === "" ? finansOnerisi : onceki));
  }, [formAcik, duzenlenen, finansOnerisi]);

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "contract", contractId, "yukumlulukler"),
    queryFn: async () =>
      (await api<ContractObligationList>(`/api/contracts/${contractId}/obligations`, { tenantId }))
        .data,
  });

  const uyeler = useQuery({
    enabled: formAcik,
    ...sozlesmeAdaylariSorgusu(tenantId, contractId),
  });
  /** ⚠ Aktiflik süzgeci SUNUCUDA (BUG-002): liste zaten yalnız aktif üyelikleri taşıyor. */
  const atanabilirUyeler = uyeler.data?.candidates ?? [];

  const yenile = () =>
    queryClient.invalidateQueries({
      queryKey: tenantKey(tenantId, "contract", contractId, "yukumlulukler"),
    });

  /**
   * YÜKÜMLÜLÜK GÜNCELLEME (CLOS-1109 · `PUT /contracts/:id/obligations/:obligationId`).
   *
   * ### ⚠ Uç yazılmıştı, çağıran yoktu
   *
   * Ekran yükümlülük OLUŞTURABİLİYOR ve DURUMUNU değiştirebiliyordu (`/transitions`), ama
   * yanlış yazılmış bir başlığı ya da tarihi düzeltmenin hiçbir yolu yoktu.
   *
   * ⚠ `revision` EŞZAMANLILIK KİLİDİ: şema onu zorunlu tutuyor. İki kullanıcı aynı
   * yükümlülüğü düzenlerse ikincisi sunucudan ret alır — "son yazan kazanır" sessiz veri
   * kaybı olurdu. Değer düzenlenen SATIRDAN geliyor, ekranda tutulan bir sayaçtan değil.
   */
  const guncelle = useMutation({
    mutationFn: () =>
      api<ContractObligation>(`/api/contracts/${contractId}/obligations/${duzenlenen!.id}`, {
        method: "PUT",
        tenantId,
        body: JSON.stringify({
          title: baslik,
          dueDate: sonTarih || undefined,
          repeatFreq: tekrar,
          repeatInterval: tekrar === "none" ? undefined : Number(tekrarAraligi),
          responsibleMembershipId: sorumlu || undefined,
          revision: duzenlenen!.revision,
        }),
      }),
    onSuccess: async () => {
      setFormAcik(false);
      setDuzenlenen(null);
      setBaslik("");
      setSonTarih("");
      setTekrar("none");
      setSorumlu("");
      await yenile();
      toast.success("Yükümlülük güncellendi");
    },
    /** ⚠ Sunucunun cümlesi: sürüm çakışması kendi nedenini taşır. */
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Güncellenemedi"),
  });

  const olustur = useMutation({
    mutationFn: () =>
      api<ContractObligation>(`/api/contracts/${contractId}/obligations`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({
          title: baslik,
          dueDate: sonTarih || undefined,
          repeatFreq: tekrar,
          repeatInterval: tekrar === "none" ? undefined : Number(tekrarAraligi),
          responsibleMembershipId: sorumlu || undefined,
        }),
      }),
    onSuccess: async () => {
      setFormAcik(false);
      setBaslik("");
      setSonTarih("");
      setTekrar("none");
      setSorumlu("");
      await yenile();
      toast.success("Yükümlülük oluşturuldu");
    },
    onError: (hata) =>
      toast.error(hata instanceof ApiRequestError ? hata.message : "Oluşturulamadı"),
  });

  const gecis = useMutation({
    mutationFn: ({
      id,
      to,
      revision,
    }: {
      id: string;
      to: ContractObligationStatus;
      revision: number;
    }) =>
      api(`/api/contracts/${contractId}/obligations/${id}/transitions`, {
        method: "POST",
        tenantId,
        body: JSON.stringify({ to, revision }),
      }),
    onSuccess: async () => {
      await yenile();
      toast.success("Durum güncellendi");
    },
    onError: (hata) =>
      toast.error(
        hata instanceof ApiRequestError
          ? hata.message
          : "Durum değiştirilemedi; sayfayı yenileyip tekrar deneyin",
      ),
  });

  return (
    <Card
      title="Yükümlülükler"
      actions={
        yazabilir ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              /** ⚠ Vazgeçmek düzenleme hedefini de bırakır; yoksa form bir dahaki açılışta
                  eski kaydı güncellemeye devam ederdi. */
              setDuzenlenen(null);
              /** Açılışta Finans Sorumlusu önerisi efektte (K4); her açılış için yeniden. */
              setSorumlu("");
              oneriUygulandi.current = false;
              setFormAcik((a) => !a);
            }}
          >
            {formAcik ? "Vazgeç" : "Yükümlülük ekle"}
          </Button>
        ) : null
      }
    >
      {sorgu.isPending ? (
        <div role="status" aria-label="Yükümlülükler yükleniyor">
          <Skeleton lines={2} />
        </div>
      ) : sorgu.error || !sorgu.data ? (
        <StateView
          state={resolveErrorState(sorgu.error, { idempotent: true })}
          onRetry={() => void sorgu.refetch()}
        />
      ) : sorgu.data.obligations.length === 0 ? (
        <EmptyState
          title="Yükümlülük yok"
          description="Bu sözleşmeye henüz bir yükümlülük eklenmedi."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-border-default">
          {sorgu.data.obligations.map((y) => (
            <li key={y.id} className="flex flex-col gap-1 py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-fg-strong">{y.title}</span>
                <Badge tone={STATUS_TONE[y.status]}>
                  {CONTRACT_OBLIGATION_STATUS_NAMES[y.status]}
                </Badge>
                {y.sourceType !== "manual" && <Badge tone="neutral">AI kaynaklı</Badge>}
              </div>
              <p className="text-xs text-fg-soft">
                {y.dueDate
                  ? `Son tarih: ${formatDate(y.dueDate, preferences.locale)}`
                  : "Son tarihsiz"}
                {y.repeatFreq !== "none" ? ` · ${REPEAT_LABELS[y.repeatFreq]}` : ""}
                {y.responsibleName ? ` · Sorumlu: ${y.responsibleName}` : ""}
                {y.partyName ? ` · Taraf: ${y.partyName}` : ""}
              </p>
              {yazabilir && (
                <div className="flex flex-wrap gap-2">
                  {/*
                    ⚠ DÜZENLEME, DURUM GEÇİŞİNDEN AYRI (CLOS-1109): geçişler yükümlülüğün
                    yaşam döngüsünü ilerletir, düzenleme İÇERİĞİNİ düzeltir. Aynı düğme
                    grubuna koymak, "tamamlandı" ile "başlığı yanlış yazmışım"ı aynı karar
                    gibi gösterirdi.
                  */}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setDuzenlenen(y);
                      setBaslik(y.title);
                      setSonTarih(y.dueDate ? y.dueDate.slice(0, 10) : "");
                      setTekrar(y.repeatFreq);
                      setTekrarAraligi(String(y.repeatInterval ?? 1));
                      setSorumlu(y.responsibleMembershipId ?? "");
                      setFormAcik(true);
                    }}
                  >
                    Düzenle
                  </Button>
                  {TRANSITIONS[y.status].map((t) => (
                    <Button
                      key={t.to}
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={gecis.isPending}
                      onClick={() => gecis.mutate({ id: y.id, to: t.to, revision: y.revision })}
                    >
                      {t.label}
                    </Button>
                  ))}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {formAcik && yazabilir && (
        <div className="mt-4 flex flex-col gap-3 border-t border-border-default pt-4">
          <FormField label="Başlık" required>
            <Input
              value={baslik}
              onChange={(e) => setBaslik(e.target.value)}
              placeholder="Örn. Aylık rapor teslimi"
            />
          </FormField>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Son tarih">
              <Input type="date" value={sonTarih} onChange={(e) => setSonTarih(e.target.value)} />
            </FormField>
            <FormField label="Sorumlu">
              <Select value={sorumlu} onChange={(e) => setSorumlu(e.target.value)}>
                <option value="">Seçilmedi</option>
                {atanabilirUyeler.map((u) => (
                  <option key={u.membershipId} value={u.membershipId}>
                    {u.name}
                  </option>
                ))}
              </Select>
            </FormField>
            <FormField label="Tekrar">
              <Select value={tekrar} onChange={(e) => setTekrar(e.target.value as typeof tekrar)}>
                {Object.entries(REPEAT_LABELS).map(([deger, etiket]) => (
                  <option key={deger} value={deger}>
                    {etiket}
                  </option>
                ))}
              </Select>
            </FormField>
            {tekrar !== "none" && (
              <FormField label="Tekrar aralığı">
                <Input
                  type="number"
                  min={1}
                  value={tekrarAraligi}
                  onChange={(e) => setTekrarAraligi(e.target.value)}
                />
              </FormField>
            )}
          </div>
          <div>
            <Button
              type="button"
              loading={olustur.isPending || guncelle.isPending}
              disabled={baslik.trim() === ""}
              onClick={() => (duzenlenen ? guncelle.mutate() : olustur.mutate())}
            >
              Kaydet
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
